import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { AxiosResponse } from 'axios';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { SpinnerContainer } from '@/components/Containers/SpinnerContainer';
import { iFormContactFields } from '@/components/Form/FormContact';
import { iFormContactGuessUserFields } from '@/components/Form/FormContactGuessUser';
import { FeatureComparatorPublicPage } from '@/components/PublicPages/FeatureComparatorPublicPage';
import { BillingDetails, getBillingDetails } from '@/features/billing';
import { Country, getCountries } from '@/features/countries';
import { useCreateInvoicePending } from '@/features/invoices';
import { getUserProducts } from '@/features/myProducts';
import {
  useCreateNotificationContact,
  useCreateNotificationContactGuessUser,
} from '@/features/notification';
import {
  UpdatePaymentIntent,
  useDeleteUserIncompleteProduct,
  useUpdatePayment,
} from '@/features/payment';
import { useProducts } from '@/features/products';
import { getSmsCredits, SmsCredits } from '@/features/smsCredits';
import { useMe } from '@/hooks/useMe';
import { useAuth } from '@/lib/auth';
import { getAllAxiosData, GetterAxios } from '@/lib/axios';
import { buildGTMAddLogged, buildGTMAddNotLogged, sendEventGtm } from '@/lib/gtm/gtmUtils';
import { Currency, IntervalBillingMode, ProductTypes, UserProductTypes } from '@/types';
import {
  getFreemiumProduct,
  getPricesIdsByCurrencyAndInterval,
  getProductsNonFreemium,
  getUserData,
  isLanguage,
} from '@/utils/helpers';
import storage from '@/utils/storage';

export type FeatureComparatorPageProps = {
  publicPage?: boolean;
  isLogged?: boolean;
};

export const FeatureComparatorPage = ({
  publicPage = false,
  isLogged = false,
}: FeatureComparatorPageProps) => {
  const handleError = useErrorHandler();
  const productsQuery = useProducts();
  const { lang } = useParams();
  const { isLogged: isLoggedAuth } = useAuth();

  isLogged || (isLogged = isLoggedAuth());

  const sendDataToGTM = useGTMDispatch();

  const { i18n } = useTranslation();

  const [countries, setCountries] = useState<Country[]>([]);
  const [smsCredits, setSmsCredits] = useState<SmsCredits | undefined>(undefined);
  const [fetchError, setFetchError] = useState(false);
  const { getMe, isMeSet } = useMe();

  const [billingDetails, setBillingDetails] = useState<BillingDetails | undefined>(undefined);
  const [userProducts, setUserProducts] = useState<UserProductTypes[] | undefined>(undefined);
  const [hasBeenCanceled, setHasBeenCanceled] = useState<boolean>(false);

  const { isLoading: productsIsLoading = false } = productsQuery;
  const { data: products } = productsQuery;
  const createNotificationContactGuessUserMutation = useCreateNotificationContactGuessUser();
  const createNotificationContactMutation = useCreateNotificationContact();
  const deleteUserIncompleteProductMutation = useDeleteUserIncompleteProduct();
  const createUpdatePaymentMutation = useUpdatePayment();
  const createInvoicePendingMutation = useCreateInvoicePending();
  const groupId = getMe().userInfo.groupId;

  useEffect(() => {
    if (isLanguage(lang)) {
      void i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  useEffect(() => {
    if (!productsIsLoading && products) {
      const getters: GetterAxios[] = [
        { callback: getCountries },
        { callback: getSmsCredits, parameters: {} },
      ];
      const setters: any = [setCountries, setSmsCredits];

      const pageData = getAllAxiosData(getters, setters);

      pageData.then((response) => {
        if (response?.type === 'error') {
          setFetchError(true);
          setSmsCredits({ '--': '0' });
        } else {
          setFetchError(false);
        }
      });

      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [productsIsLoading, products, handleError]);

  useEffect(() => {
    if (isMeSet() && groupId) {
      const getters: GetterAxios[] = [
        { callback: getBillingDetails, parameters: { groupId: groupId } },
        { callback: getUserProducts, parameters: { groupId: groupId } },
      ];
      const setters: any = [setBillingDetails, setUserProducts];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [handleError, groupId, isMeSet]);

  useEffect(() => {
    if (hasBeenCanceled && groupId) {
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId: groupId } },
      ];
      const setters: any = [setUserProducts];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [groupId, handleError, hasBeenCanceled]);

  if ((productsIsLoading && !fetchError) || !smsCredits) {
    return <SpinnerContainer />;
  }

  if (!products?.length) {
    return (
      <div
        role="list"
        aria-label="countries"
        className="bg-white text-gray-500 h-40 flex justify-center items-center flex-col"
      >
        <h4 className="md__payments__title--h4">No Products Found</h4>
      </div>
    );
  }

  const onGuestRequestInfoSubmit = async (values: iFormContactGuessUserFields) => {
    sendEventGtm(buildGTMAddNotLogged(), sendDataToGTM);
    await createNotificationContactGuessUserMutation.mutateAsync({
      data: values,
    });
  };

  const onRegisteredRequestInfoSubmit = async (values: iFormContactFields) => {
    sendEventGtm(buildGTMAddLogged(userData), sendDataToGTM);
    await createNotificationContactMutation.mutateAsync({
      data: values,
    });
  };

  const updatePayment = async (
    product: ProductTypes,
    currency: Currency,
    intervalCount: IntervalBillingMode,
    onErrorCallback: (status: number, urlRedirect?: string, detail?: string) => void
  ): Promise<UpdatePaymentIntent | AxiosResponse | void> => {
    const prices: string[] = getPricesIdsByCurrencyAndInterval(
      product.prices,
      currency,
      intervalCount
    );

    if (!prices) {
      return;
    }

    const paymentIntent = await createUpdatePaymentMutation.mutateAsync({
      prices: prices,
      onErrorCallback: onErrorCallback,
    });

    storage.clearUserData();
    setHasBeenCanceled(false);

    return paymentIntent;
  };

  const onCancelIncompleteAction = async () => {
    storage.clearUserData();
    const userProducts = await deleteUserIncompleteProductMutation.mutateAsync({
      groupId: groupId,
    });
    setUserProducts(userProducts);
  };

  const onConfirmUnpaidMeteredErrorAction = async () => {
    await createInvoicePendingMutation.mutateAsync();
  };

  const userData = getUserData(getMe(), i18n);
  return (
    <FeatureComparatorPublicPage
      countries={countries}
      onGuestRequestInfoSubmit={onGuestRequestInfoSubmit}
      onRegisteredRequestInfoSubmit={onRegisteredRequestInfoSubmit}
      freemiumProduct={getFreemiumProduct(products)}
      products={getProductsNonFreemium(products)}
      billingDetails={billingDetails}
      userProducts={userProducts}
      publicPage={publicPage}
      isLogged={isLogged}
      userData={userData}
      getUpdatePaymentIntent={updatePayment}
      onCancelIncompleteAction={onCancelIncompleteAction}
      onConfirmUnpaidMeteredErrorAction={onConfirmUnpaidMeteredErrorAction}
    />
  );
};
